import { scimConnectionTypes } from 'client/util/Constants/ApplicationConstants';
import CollectionApi from 'api/v2/CollectionApi';

const { SCIM } = scimConnectionTypes;
/**
 * Translates jumpcloud's default sort param to Identity Management Application's sort params
 *
 * Example:
 * - lastname => sortOrder=asc
 * - -lastname => sortOrder=desc
 * @see https://datatracker.ietf.org/doc/html/rfc7644#section-3.4.2.3
 * @see https://docs.jumpcloud.com/2.0/applications/get-a-list-of-users-to-import-from-an-application-idm-service-provider
 */
const parseSortParams = (sort) => {
  const sortParts = sort.split('-');
  let sortParams = {};
  if (sortParts.length === 1) sortParams.sortOrder = 'asc';
  // Idm applications endpoint doesn't accept the '-' in the sort field
  // Passing only sort field
  else sortParams = { sort: sortParts[1], sortOrder: 'desc' };
  return sortParams;
};

/**
 * Returns input string with base64 encoding
 * @param {String} string input string
 */
const encodeToBase64 = (string) => window.btoa(string);
class ScimUsersImportApi extends CollectionApi {
  constructor() {
    super('/api/v2/applications', {
      skip: 0,
      limit: 50,
      sort: '',
      filter: '',
    }, 100, false, true);
    this.applicationSourceName = '';
    this.importInjectionQuery = '';
    this.applicationConnectionType = '';
  }

  getScheduledImports() {
    return this.axios.get(`${this.baseUrl}/schedules`)
      .then(({ data: { schedules: [scheduledJob] } }) => ({
        allowUserReactivation: scheduledJob?.allowUserReactivation,
        importInjectionQuery: scheduledJob?.query,
        scheduledJobId: scheduledJob?.scheduledJobId,
        sendEmail: scheduledJob?.sendEmail,
      }));
  }

  async fetch(skip = 0, limit = 50, sort = '', filter = '') {
    const params = {
      skip,
      limit,
      sort,
      filter,
    };

    if (sort) {
      const sortParams = parseSortParams(sort);
      Object.assign(params, sortParams);
    }

    let importRequestUrl = '';
    if (this.applicationConnectionType === SCIM) {
      importRequestUrl = `${this.baseUserImportUrl}?${this.param(params)}`;
    } else {
      const query = new URLSearchParams(this.importInjectionQuery);
      // remove empty values
      const entries = Array.from(query.entries()).filter((pair) => Boolean(pair[1]));
      const cleanQuery = entries
        .reduce((previous, current) => `${previous}${previous ? '&' : ''}${current.join('=')}`, '');
      const queryString = encodeURI(cleanQuery);
      const encodedQuery = `query=${encodeToBase64(queryString)}`;

      importRequestUrl = `${this.baseUserImportUrl}?${this.param(params)}&${encodedQuery}`;
    }

    return this.initialize()
      .then(() => this.axios.get(importRequestUrl))
      .then(({ data }) => ({
        results: data.users,
        totalCount: data.total_count,
      }));
  }

  setScimApplicationId(scimAppId = '') {
    this.baseUrl = `/api/v2/applications/${scimAppId}/import`;
    this.baseUserImportUrl = `${this.baseUrl}/users`;
  }

  setScimApplicationSourceName(scimAppSourceName = '') {
    this.applicationSourceName = scimAppSourceName;
  }

  setImportInjectionQuery(query = '') {
    this.importInjectionQuery = query;
  }

  setApplicationConnectionType(connectionType = SCIM) {
    this.applicationConnectionType = connectionType;
  }

  /**
   * Submits an update to the user import schedule service.
   */
  updateImportSchedule(payload) {
    return this.axios.post(`${this.baseUrl}/schedules`, payload)
      .then(({ data: { scheduledJobId } }) => scheduledJobId);
  }

  deleteImportSchedule(scheduleId) {
    return this.axios.delete(`${this.baseUrl}/schedules/${scheduleId}`, {});
  }
}

export default new ScimUsersImportApi();
